import { css } from '@stitches/react';
import { Reaction, Vector } from '@retro-ui/api-contract';
import { Button, Popover, styled } from '@moleculeone/ui';
import { useState } from 'react';
import { ISynthesis } from '@moleculeone/tree';

import { saveAsJson } from '@f/utils/export';

function buildReferenceReactionFormat(reactionData: Reaction) {
  const { productSmiles, substrateSmiles, productToMap, substratesToMap } = reactionData;

  return {
    product: productSmiles,
    substrates: substrateSmiles,
    product_to_map: productToMap,
    substrates_to_map: substratesToMap,
  };
}

function buildReactatorFormat(reactionSmiles: string, productSmiles: string) {
  const parsedSmiles = reactionSmiles.split('>>', 2);

  return [
    {
      target: productSmiles,
      best_path_smiles: [
        {
          product: parsedSmiles[1],
          substrate: parsedSmiles[0],
        },
      ],
    },
  ];
}

interface DebugTreeLabelProps {
  point: Vector;
  scale: number;
  padding: number;
  substrateId: string;
  reactionId: string;
  fixedReactionPrediction: number;
  reactionData: Reaction;
  synthesis: ISynthesis;
}

const debugLabelStyle = css({
  color: '#333',
  backgroundColor: '#ebebeb',
  padding: '$3',
  whiteSpace: 'nowrap',
  fontSize: '0.8em',
  fontFamily: '$mono',
  textAlign: 'left',
  overflow: 'visible',
  position: 'absolute',
});

const TriggerButton = styled(Button, {
  width: '100%',
  fontFamily: '$mono',
});

const ExportButton = styled(Button, {
  fontFamily: '$mono',
});

export function DebugTreeLabel(props: DebugTreeLabelProps) {
  const { point, scale, padding, reactionId, fixedReactionPrediction, reactionData, synthesis } = props;

  const labelPointProps = {
    x: point[0],
    y: point[1],
    width: 1,
    height: 1,
  };

  const [isOpen, setIsOpen] = useState(false);

  function exportObjectWithMapping() {
    const res = buildReferenceReactionFormat(reactionData);
    saveAsJson(res, `reaction_${reactionId}-rr-object`);
  }

  function exportRxn() {
    const res = synthesis.reactions[reactionId].smiles;
    saveAsJson(res, `reaction_${reactionId}-rxn`, false);
  }

  function exportReactionForReactator() {
    const reactionSmiles = synthesis.reactions[reactionId].smiles;
    const res = buildReactatorFormat(reactionSmiles, reactionData.productSmiles);
    saveAsJson(res, `reaction_${reactionId}-reactator-object`);
  }

  function handleExport(format: string) {
    switch (format) {
      case 'reference-reaction':
        exportObjectWithMapping();
        break;
      case 'rxn':
        exportRxn();
        break;
      case 'reactator':
        exportReactionForReactator();
        break;
      default:
        break;
    }
    setIsOpen(false);
  }

  return (
    <foreignObject
      x={labelPointProps.x - labelPointProps.width / 2 / scale - padding / scale}
      y={labelPointProps.y - labelPointProps.height / 2}
      width={(labelPointProps.width + padding * 2) / scale}
      height={(labelPointProps.height + padding * 2) / scale}
      style={{
        transform: `scale(${scale * 0.75}, ${scale * 0.75})`,
        transformOrigin: `${labelPointProps.x - labelPointProps.width / 2 / scale}px ${
          labelPointProps.y - labelPointProps.height / scale
        }px`,
        overflow: 'visible',
      }}
    >
      <div className={debugLabelStyle()}>
        <div>{`ML Score: ${fixedReactionPrediction}`}</div>
        <div>{`Reaction id: ${reactionId}`}</div>
        <div>
          <Popover
            hasArrow
            hasClose={false}
            trigger={<TriggerButton theme="purple" weight="minimal" size="small" active={isOpen} text="Export" />}
            contentProps={{
              css: {
                display: 'flex',
                flexDirection: 'column',
                gap: '$3',
              },
            }}
            isOpen={isOpen}
            onOpenChange={(isOpen: boolean) => {
              setIsOpen(isOpen);
            }}
          >
            <ExportButton
              weight="outlined"
              size="small"
              text="reference reactions format"
              onClick={() => handleExport('reference-reaction')}
            />
            <ExportButton weight="outlined" size="small" text="rxn format" onClick={() => handleExport('rxn')} />
            <ExportButton
              weight="outlined"
              size="small"
              text="reactator format"
              onClick={() => handleExport('reactator')}
            />
          </Popover>
        </div>
      </div>
    </foreignObject>
  );
}
